/** @file One animation's keys in the form the key commands take: names, absolute values (R17-03). */
import { throwError } from '@pose2d/format/throwError';
import type { AnimationDoc, Doc } from '@pose2d/format/types';
import { absoluteValue } from './absoluteValue';
import { clipText } from './clipText';
import { compareText } from './compareText';
import type { EntitySelection } from './entitySelection';
import { IK_CHANNELS } from './ikChannels.data';
import { nameIn } from './nameIn';

/** One key and where it goes in the reply: `[group, target, channel]`, `['deform', attachment]` or `[group]`. */
export type KeyPiece = { path: string[]; value: Record<string, unknown> };

type Key = { f: number; v?: unknown; curve?: unknown };
type Tracks = Record<string, Partial<Record<string, Key[]>> | undefined>;
type Table = Record<string, { name: string }>;
type Group = { name: 'bones' | 'slots' | 'ik'; table: Table; props: readonly string[] };
/** The selection as predicates over channels and frames. */
type Filter = { targets: string[] | undefined; wanted: (channel: string) => boolean; inRange: (key: { f: number }) => boolean };

const TRANSFORM = ['rotation', 'x', 'y', 'scaleX', 'scaleY', 'shearX', 'shearY'];
const SLOT_PROPS = ['attachment', 'rgb', 'alpha', 'dark'];
const channelOf = (group: Group['name'], prop: string): string => (group === 'ik' ? (IK_CHANNELS[prop] ?? prop) : prop);
const withCurve = (curve: unknown): Record<string, unknown> => (curve === undefined ? {} : { curve });

/** Bone transform keys back to absolute values (setKey stores them setup-relative); every other value is stored as given. */
function valueOf(doc: Doc, group: Group['name'], id: string, prop: string, value: unknown): unknown {
  const setup = group === 'bones' && TRANSFORM.includes(prop) ? doc.bones[id]?.setup : undefined;
  if (setup === undefined || typeof value !== 'number') return value;
  return absoluteValue(value, setup[prop as keyof typeof setup], prop === 'scaleX' || prop === 'scaleY');
}

const bezierOf = (curve: unknown): [number, number, number, number] | undefined =>
  typeof curve === 'object' && curve !== null && 'bezier' in curve ? (curve as { bezier: [number, number, number, number] }).bezier : undefined;

/**
 * A scale key's bezier handles back to channel units: animation.setKey and setCurve store their `dv` divided by the
 * setup scale like the value (T-232, final review m1), so they come back multiplied, exactly inverted. Other curves and
 * channels are stored as given.
 */
function curveOf(doc: Doc, group: Group['name'], id: string, prop: string, curve: unknown): unknown {
  const setup = group === 'bones' && (prop === 'scaleX' || prop === 'scaleY') ? doc.bones[id]?.setup[prop] : undefined;
  const bezier = bezierOf(curve);
  if (!setup || bezier === undefined) return curve;
  const [x1, dv1, x2, dv2] = bezier;
  return { bezier: [x1, absoluteValue(dv1, setup, true), x2, absoluteValue(dv2, setup, true)] };
}

/** Ids of `table` that `tracks` keys, in the order of `targets` (when given) or by name. */
function targetIds(tracks: Record<string, unknown>, table: Table, targets: string[] | undefined): string[] {
  const keyed = Object.keys(tracks);
  if (targets === undefined) return keyed.sort((a, b) => compareText(table[a]?.name ?? a, table[b]?.name ?? b));
  return [...new Set(targets.flatMap((name) => keyed.filter((id) => table[id]?.name === name)))];
}

/** NOT_FOUND at the `fields[i]` entry of a `targets:` name that is no bone, slot, IK constraint or attachment. */
function checkTargets(doc: Doc, sel: EntitySelection): void {
  const tables: Table[] = [doc.bones, doc.slots, doc.constraints, doc.attachments];
  const unknown = sel.targets?.find((name) => !tables.some((table) => Object.values(table).some((entity) => entity.name === name)));
  if (unknown === undefined) return;
  const at = sel.targetAt?.[unknown];
  throwError('NOT_FOUND', { kind: 'target', ref: clipText(unknown, 200) }, { path: at === undefined ? 'fields' : `fields[${String(at)}]`, hint: 'targets: names bones, slots, IK constraints or mesh attachments' });
}

/** Bone, slot and IK keys as `{ frame, value, curve? }`, per target, channels in setKey order. */
function channelKeys(doc: Doc, anim: AnimationDoc, filter: Filter, out: KeyPiece[]): void {
  const groups: Group[] = [
    { name: 'bones', table: doc.bones, props: [...TRANSFORM, 'inherit'] },
    { name: 'slots', table: doc.slots, props: SLOT_PROPS },
    { name: 'ik', table: doc.constraints, props: Object.keys(IK_CHANNELS) },
  ];
  for (const group of groups) {
    const tracks = anim[group.name] as Tracks;
    for (const id of targetIds(tracks, group.table, filter.targets)) trackKeys(doc, group, id, tracks[id] ?? {}, filter, out);
  }
}

/** One target's keys of `group`, channel by channel. */
function trackKeys(doc: Doc, group: Group, id: string, track: Partial<Record<string, Key[]>>, filter: Filter, out: KeyPiece[]): void {
  const target = group.table[id]?.name ?? id;
  for (const prop of group.props) {
    const channel = channelOf(group.name, prop);
    if (!filter.wanted(channel)) continue;
    const keys = (track[prop] ?? []).filter(filter.inRange);
    for (const key of keys) {
      out.push({ path: [group.name, target, channel], value: { frame: key.f, value: valueOf(doc, group.name, id, prop, key.v), ...withCurve(curveOf(doc, group.name, id, prop, key.curve)) } });
    }
  }
}

/** Deform keys per mesh attachment as `{ frame, offsets, curve? }` (animation.setDeformKey's relative form). */
function deformKeys(doc: Doc, anim: AnimationDoc, filter: Filter, out: KeyPiece[]): void {
  if (!filter.wanted('deform')) return;
  const attName = nameIn(doc.attachments);
  for (const id of targetIds(anim.deform, doc.attachments, filter.targets)) {
    for (const key of (anim.deform[id] ?? []).filter(filter.inRange)) out.push({ path: ['deform', attName(id)], value: { frame: key.f, offsets: key.offsets, ...withCurve(key.curve) } });
  }
}

/** Draw-order keys (slot names) and event keys (event names), as setDrawOrderKey / setEventKey take them. */
function untargetedKeys(doc: Doc, anim: AnimationDoc, filter: Filter & { named: (channel: string) => boolean }, out: KeyPiece[]): void {
  const slotName = nameIn(doc.slots);
  const eventName = nameIn(doc.events);
  if (filter.named('drawOrder')) for (const key of anim.drawOrder.filter(filter.inRange)) out.push({ path: ['drawOrder'], value: { frame: key.f, order: key.order?.map(slotName) ?? null } });
  if (filter.named('events')) for (const { f, event, ...overrides } of anim.events.filter(filter.inRange)) out.push({ path: ['events'], value: { frame: f, event: eventName(event), ...overrides } });
}

/**
 * The selected keys in reply order: bones, slots, IK constraints (per target, channels in setKey order), then deform
 * keys per mesh attachment, draw-order keys and event keys. Values are what the key commands take back unchanged:
 * bone transforms absolute (absoluteValue), slot and IK values as stored, ids as names. A `targets:` selection leaves
 * out draw order and events unless `channels:` names them.
 */
export function animationKeys(doc: Doc, anim: AnimationDoc, sel: EntitySelection): KeyPiece[] {
  checkTargets(doc, sel);
  const filter: Filter = {
    targets: sel.targets,
    wanted: (channel) => sel.channels === undefined || sel.channels.includes(channel),
    inRange: (key) => sel.frames === undefined || (key.f >= sel.frames[0] && key.f <= sel.frames[1]),
  };
  const named = (channel: string): boolean => (sel.targets === undefined && filter.wanted(channel)) || (sel.channels?.includes(channel) ?? false);
  const out: KeyPiece[] = [];
  channelKeys(doc, anim, filter, out);
  deformKeys(doc, anim, filter, out);
  untargetedKeys(doc, anim, { ...filter, named }, out);
  return out;
}
